import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppleFilled } from '@ant-design/icons';
import { setLocalStorage } from '../../utils/func';
import finishSignIn from '../../utils/finishSignIn';
import { abortable } from '../../utils/abortable';
import { rememberOAuthConsent, consumeOAuthConsent, consumeOAuthAttempt, clearOAuthCallback, redirectToOAuth } from '../AuthButtonGoogleCustom/oauthConsent';

export default function AuthButtonApple({ disabled = true, onBusy }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const pending = useRef(false);
  const processedCallback = useRef('');
  const mounted = useRef(false);
  const generation = useRef(0);
  const completionController = useRef(null);

  useEffect(() => {
    mounted.current = true;
    const restore = (event) => {
      if (!event.persisted) return;
      generation.current += 1;
      completionController.current?.abort();
      consumeOAuthConsent('apple', pending.current);
      pending.current = false;
      setRedirecting(false);
      setFinishing(false);
    };
    window.addEventListener('pageshow', restore);
    return () => {
      mounted.current = false;
      window.removeEventListener('pageshow', restore);
      // React StrictMode replays this effect without abandoning the callback.
      // Abort genuine departures after that synchronous replay has finished.
      const controller = completionController.current;
      Promise.resolve().then(() => {
        if (!mounted.current && completionController.current === controller) controller?.abort();
      });
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.slice(1));
    const payload = params.get('apple_auth');
    const denied = params.get('apple_error');
    if ((!payload && !denied) || processedCallback.current === hash) return;
    processedCallback.current = hash;
    const request = ++generation.current;
    const current = () => mounted.current && generation.current === request;
    completionController.current?.abort();
    const accepted = consumeOAuthAttempt('apple', params.get('apple_state'));
    clearOAuthCallback(['apple_auth', 'apple_error', 'apple_state']);
    pending.current = false;
    setRedirecting(false);
    if (!accepted || denied) {
      setError('brand:authError');
      return;
    }
    setFinishing(true);
    setError('');
    const controller = new AbortController();
    completionController.current = controller;
    const timer = setTimeout(() => controller.abort(), 25000);
    const complete = async () => {
      try {
        await abortable(finishSignIn(JSON.parse(payload), { i18n, navigate, isCurrent: current,
          registration: accepted.registration, signal: controller.signal }), controller.signal);
      } catch {
        if (current()) setError('brand:authError');
      } finally {
        clearTimeout(timer);
        if (completionController.current === controller) completionController.current = null;
        if (current()) setFinishing(false);
      }
    };
    complete();
  }, [location, navigate, i18n]);

  const busy = redirecting || finishing;
  useEffect(() => { onBusy?.(busy ? 'apple' : null); }, [busy, onBusy]);

  const signIn = () => {
    if (disabled || pending.current || finishing) return;
    setError('');
    const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    const state = clientId && rememberOAuthConsent('apple');
    if (!state) {
      setError('brand:authUnavailable');
      return;
    }
    const from = new URLSearchParams(window.location.search).get('from') || '/';
    setLocalStorage('login_from', from);
    // Keep the existing backend callback and form_post contract.
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: process.env.REACT_APP_API_URL + '/user/login_apple',
      scope: 'name email', response_type: 'code id_token', response_mode: 'form_post',
      state,
    });
    pending.current = state;
    setRedirecting(true);
    try {
      redirectToOAuth(`https://appleid.apple.com/auth/authorize?${params}`);
    } catch {
      consumeOAuthConsent('apple', state);
      pending.current = false;
      setRedirecting(false);
      setError('brand:authError');
    }
  };


  return (
    <div className="apple-button-wrapper">
      {error && <p className="error-message" role="alert">{t(error)}</p>}
      <Button style={{ width: '100%' }} className={`button_border ${busy ? 'auth-button-busy' : ''}`}
        icon={<AppleFilled aria-hidden="true" />} disabled={disabled || busy} onClick={signIn} aria-busy={busy}>
        <span className="auth-button-label">{t('login.button_apple')}</span>
      </Button>
    </div>
  );
}
